import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Eye, Dumbbell, Salad, BookOpen, ChevronDown, ChevronUp, ExternalLink, Ruler, Calendar as CalendarIcon } from 'lucide-react';
import CalendarView from '../components/CalendarView';

const BACKEND_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:8000'
  : (process.env.REACT_APP_BACKEND_URL || '');

const DAYS = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];

function statusBadge(assignment) {
  const today = new Date().toISOString().slice(0, 10);
  if (assignment.endDate && assignment.endDate < today) return { label: 'Encerrado', cls: 'text-[#555] border-white/10' };
  if (assignment.startDate && assignment.startDate > today) return { label: 'Próximo', cls: 'text-yellow-400 border-yellow-400/30' };
  return { label: 'Ativo', cls: 'text-green-400 border-green-500/30' };
}

export default function PreviewArea() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get('token');

  const [tab, setTab] = useState('workout');
  const [student, setStudent] = useState(null);
  const [workout, setWorkout] = useState(null);
  const [diet, setDiet] = useState(null);
  const [contents, setContents] = useState([]);
  const [workoutAssignments, setWorkoutAssignments] = useState([]);
  const [dietAssignments, setDietAssignments] = useState([]);
  const [studentProfile, setStudentProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openMeal, setOpenMeal] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) return;

    const fetches = [
      axios.get(`${BACKEND_URL}/api/admin/students`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${BACKEND_URL}/api/admin/students/${id}/workout`, { headers }).catch(() => ({ data: null })),
      axios.get(`${BACKEND_URL}/api/admin/students/${id}/diet`, { headers }).catch(() => ({ data: null })),
      axios.get(`${BACKEND_URL}/api/contents`).catch(() => ({ data: [] })),
      axios.get(`${BACKEND_URL}/api/admin/students/${id}/workout-assignments`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${BACKEND_URL}/api/admin/students/${id}/diet-assignments`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${BACKEND_URL}/api/admin/students/${id}/profile`, { headers }).catch(() => ({ data: {} })),
    ];

    Promise.all(fetches)
      .then(([studentsRes, wRes, dRes, contentsRes, waRes, daRes, profileRes]) => {
        const found = studentsRes.data.find(s => s.id === id);
        setStudent(found || { name: 'Aluno', email: '' });
        setWorkout(wRes.data);
        setDiet(dRes.data);
        setContents(contentsRes.data || []);
        setWorkoutAssignments(waRes.data || []);
        setDietAssignments(daRes.data || []);
        setStudentProfile(profileRes.data || {});
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line
  }, [id, token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-white" />
      </div>
    );
  }

  const totalCals = diet?.meals?.reduce((sum, m) => sum + m.foods.reduce((s, f) => s + (Number(f.calories) || 0), 0), 0) || 0;
  const totalProt = diet?.meals?.reduce((sum, m) => sum + m.foods.reduce((s, f) => s + (Number(f.protein) || 0), 0), 0) || 0;

  const tabs = [
    ['workout', <Dumbbell size={14} />, 'Treino'],
    ['diet', <Salad size={14} />, 'Dieta'],
    ['measurements', <Ruler size={14} />, 'Medidas'],
    ['calendar', <CalendarIcon size={14} />, 'Calendário'],
    ['content', <BookOpen size={14} />, 'Conteúdos'],
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] pb-20">
      {/* Preview banner */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-yellow-500 text-black text-xs font-bold text-center py-2 flex items-center justify-center gap-3">
        <Eye size={14} /> Visualizando como aluno (modo preview)
        <button onClick={() => window.close()} className="underline">Fechar</button>
      </div>

      <div className="pt-8 max-w-6xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="pt-4 pb-6">
          <h1 className="text-2xl font-bold tracking-tight uppercase">Área do Aluno</h1>
          {student && <p className="text-[#A3A3A3] text-sm mt-1">Visualizando como: <span className="text-white">{student.name}</span></p>}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10 mb-8 overflow-x-auto hide-scrollbar">
          {tabs.map(([key, icon, label]) => (
            <button key={key} onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-4 py-3 text-xs sm:text-sm font-medium uppercase tracking-wider transition-colors border-b-2 -mb-px whitespace-nowrap ${tab === key ? 'text-white border-white' : 'text-[#555] border-transparent hover:text-[#A3A3A3]'}`}
            >
              {icon} {label}
            </button>
          ))}
        </div>

        {/* ── TREINO ── */}
        {tab === 'workout' && (
          <>
            {workoutAssignments.length > 0 ? (
              <div className="flex flex-col gap-6">
                {workoutAssignments.map(assignment => {
                  const badge = statusBadge(assignment);
                  return (
                    <div key={assignment.id} className="bg-[#121212] border border-white/5 p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <h2 className="text-base font-bold">{assignment.name || 'Treino'}</h2>
                            <span className={`text-xs border px-2 py-0.5 ${badge.cls}`}>{badge.label}</span>
                          </div>
                          {(assignment.startDate || assignment.endDate) && (
                            <p className="text-xs text-[#555] mt-0.5">
                              {assignment.startDate} — {assignment.endDate || 'sem prazo'}
                            </p>
                          )}
                        </div>
                      </div>

                      {assignment.scheduledDays && assignment.scheduledDays.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {DAYS.map(d => (
                            <span key={d}
                              className={`text-xs border px-3 py-1 ${assignment.scheduledDays.includes(d) ? 'border-white/30 text-white' : 'border-white/5 text-[#555]'}`}
                            >{d}</span>
                          ))}
                        </div>
                      )}

                      {assignment.exercises && assignment.exercises.length > 0 && (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm border-collapse">
                            <thead>
                              <tr className="border-b border-white/10">
                                <th className="text-left py-2 pr-4 text-xs text-[#555] uppercase tracking-widest font-medium">Exercício</th>
                                <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium">Séries</th>
                                <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium">Reps</th>
                                <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium">Carga</th>
                                <th className="text-center py-2 pl-2 text-xs text-[#555] uppercase tracking-widest font-medium hidden sm:table-cell">Descanso</th>
                              </tr>
                            </thead>
                            <tbody>
                              {assignment.exercises.map((ex, i) => (
                                <tr key={i} className="border-b border-white/5">
                                  <td className="py-3 pr-4">
                                    <p className="font-medium">{ex.name}</p>
                                    {ex.notes && <p className="text-xs text-[#555] mt-0.5">{ex.notes}</p>}
                                  </td>
                                  <td className="py-3 px-2 text-center text-[#A3A3A3]">{ex.series || '—'}</td>
                                  <td className="py-3 px-2 text-center text-[#A3A3A3]">{ex.reps || '—'}</td>
                                  <td className="py-3 px-2 text-center text-[#A3A3A3]">{ex.prescribedWeight || ex.weight || '—'}</td>
                                  <td className="py-3 pl-2 text-center text-[#A3A3A3] hidden sm:table-cell">{ex.rest || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : workout ? (
              <div className="flex flex-col gap-6">
                <div>
                  <h2 className="text-xl font-bold mb-1">{workout.name}</h2>
                  {workout.days?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {workout.days.map(d => (
                        <span key={d} className="text-xs border border-white/10 px-3 py-1 text-[#A3A3A3]">{d}</span>
                      ))}
                    </div>
                  )}
                </div>
                {workout.exercises?.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-2 pr-4 text-xs text-[#555] uppercase tracking-widest font-medium">Exercício</th>
                          <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium">Séries</th>
                          <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium">Reps</th>
                          <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium">Carga</th>
                          <th className="text-center py-2 pl-2 text-xs text-[#555] uppercase tracking-widest font-medium">Descanso</th>
                        </tr>
                      </thead>
                      <tbody>
                        {workout.exercises.map((ex, i) => (
                          <tr key={i} className="border-b border-white/5">
                            <td className="py-3 pr-4">
                              <p className="font-medium">{ex.name}</p>
                              {ex.notes && <p className="text-xs text-[#555] mt-0.5">{ex.notes}</p>}
                            </td>
                            <td className="py-3 px-2 text-center text-[#A3A3A3]">{ex.series || '—'}</td>
                            <td className="py-3 px-2 text-center text-[#A3A3A3]">{ex.reps || '—'}</td>
                            <td className="py-3 px-2 text-center text-[#A3A3A3]">{ex.weight || '—'}</td>
                            <td className="py-3 pl-2 text-center text-[#A3A3A3]">{ex.rest || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-20 text-[#555]">
                <Dumbbell size={40} className="mx-auto mb-4 opacity-20" />
                <p>Treino não configurado.</p>
              </div>
            )}
          </>
        )}

        {/* ── DIETA ── */}
        {tab === 'diet' && (
          <>
            {dietAssignments.length > 0 ? (
              <div className="flex flex-col gap-4">
                {dietAssignments.map(assignment => {
                  const badge = statusBadge(assignment);
                  return (
                    <div key={assignment.id} className="flex flex-col gap-3">
                      <div className="flex items-center gap-2">
                        <h2 className="text-base font-bold">{assignment.name || 'Dieta'}</h2>
                        <span className={`text-xs border px-2 py-0.5 ${badge.cls}`}>{badge.label}</span>
                      </div>
                      {assignment.meals?.map((meal, mi) => (
                        <div key={mi} className="bg-[#121212] border border-white/5">
                          <button
                            onClick={() => setOpenMeal(openMeal === `${assignment.id}-${mi}` ? null : `${assignment.id}-${mi}`)}
                            className="w-full flex items-center justify-between px-5 py-4 text-left"
                          >
                            <div className="flex items-center gap-3">
                              <span className="font-medium">{meal.name || `Refeição ${mi + 1}`}</span>
                              {meal.time && <span className="text-xs text-[#555]">{meal.time}</span>}
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-[#555]">
                                {(meal.foods || []).reduce((s, f) => s + (Number(f.calories) || 0), 0)} kcal
                              </span>
                              {openMeal === `${assignment.id}-${mi}` ? <ChevronUp size={14} className="text-[#555]" /> : <ChevronDown size={14} className="text-[#555]" />}
                            </div>
                          </button>
                          {openMeal === `${assignment.id}-${mi}` && (
                            <div className="border-t border-white/5 px-5 pb-4">
                              <table className="w-full text-sm mt-3">
                                <thead>
                                  <tr className="border-b border-white/5">
                                    <th className="text-left pb-2 text-xs text-[#555] font-medium">Alimento</th>
                                    <th className="text-right pb-2 text-xs text-[#555] font-medium">Qtd</th>
                                    <th className="text-right pb-2 text-xs text-[#555] font-medium">Kcal</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(meal.foods || []).map((f, fi) => (
                                    <tr key={fi} className="border-b border-white/5 last:border-0">
                                      <td className="py-2 text-[#E5E5E5]">{f.name}</td>
                                      <td className="py-2 text-right text-[#555]">{f.quantity}</td>
                                      <td className="py-2 text-right text-[#A3A3A3]">{f.calories || '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            ) : diet ? (
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold">{diet.name}</h2>
                  <div className="flex gap-4 text-sm text-[#A3A3A3]">
                    {diet.calories && <span><b className="text-white">{diet.calories}</b> kcal</span>}
                    {totalProt > 0 && <span><b className="text-white">{Math.round(totalProt)}g</b> prot</span>}
                  </div>
                </div>
                {diet.meals?.map((meal, mi) => (
                  <div key={mi} className="bg-[#121212] border border-white/5">
                    <button
                      onClick={() => setOpenMeal(openMeal === mi ? null : mi)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left"
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-medium">{meal.name || `Refeição ${mi + 1}`}</span>
                        {meal.time && <span className="text-xs text-[#555]">{meal.time}</span>}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-[#555]">
                          {meal.foods.reduce((s, f) => s + (Number(f.calories) || 0), 0)} kcal
                        </span>
                        {openMeal === mi ? <ChevronUp size={14} className="text-[#555]" /> : <ChevronDown size={14} className="text-[#555]" />}
                      </div>
                    </button>
                    {openMeal === mi && (
                      <div className="border-t border-white/5 px-5 pb-4">
                        <table className="w-full text-sm mt-3">
                          <thead>
                            <tr className="border-b border-white/5">
                              <th className="text-left pb-2 text-xs text-[#555] font-medium">Alimento</th>
                              <th className="text-right pb-2 text-xs text-[#555] font-medium">Qtd</th>
                              <th className="text-right pb-2 text-xs text-[#555] font-medium">Kcal</th>
                            </tr>
                          </thead>
                          <tbody>
                            {meal.foods.map((f, fi) => (
                              <tr key={fi} className="border-b border-white/5 last:border-0">
                                <td className="py-2 text-[#E5E5E5]">{f.name}</td>
                                <td className="py-2 text-right text-[#555]">{f.quantity}</td>
                                <td className="py-2 text-right text-[#A3A3A3]">{f.calories || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20 text-[#555]">
                <Salad size={40} className="mx-auto mb-4 opacity-20" />
                <p>Dieta não configurada.</p>
              </div>
            )}
          </>
        )}

        {/* ── CONTEÚDOS ── */}
        {tab === 'content' && (
          <>
            {contents.length === 0 ? (
              <div className="text-center py-20 text-[#555]">
                <BookOpen size={40} className="mx-auto mb-4 opacity-20" />
                <p>Nenhum conteúdo disponível ainda.</p>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {contents.map((c, i) => (
                  <a key={i} href={c.url} target="_blank" rel="noopener noreferrer"
                    className="bg-[#121212] border border-white/5 hover:border-white/20 px-5 py-4 flex items-center justify-between gap-4 transition-colors"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-sm">{c.title}</p>
                      {c.description && <p className="text-xs text-[#555] mt-0.5">{c.description}</p>}
                    </div>
                    <ExternalLink size={14} className="text-[#555] flex-shrink-0" />
                  </a>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── MEDIDAS ── */}
        {tab === 'measurements' && (
          <>
            {!studentProfile?.measurements?.length ? (
              <div className="text-center py-20 text-[#555]">
                <Ruler size={40} className="mx-auto mb-4 opacity-20" />
                <p>Nenhuma medição registrada ainda.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 pr-3 text-xs text-[#555] uppercase tracking-widest font-medium">Data</th>
                      <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium">Peso</th>
                      <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium">Altura</th>
                      <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium">% Gord.</th>
                      <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium hidden sm:table-cell">M. Musc.</th>
                      <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium hidden sm:table-cell">Cintura</th>
                      <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium hidden sm:table-cell">Quadril</th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentProfile.measurements.map((m, i) => (
                      <tr key={i} className="border-b border-white/5">
                        <td className="py-3 pr-3 text-[#A3A3A3]">{m.date || '—'}</td>
                        <td className="py-3 px-2 text-center text-[#A3A3A3]">{m.weight ? `${m.weight}kg` : '—'}</td>
                        <td className="py-3 px-2 text-center text-[#A3A3A3]">{m.height ? `${m.height}cm` : '—'}</td>
                        <td className="py-3 px-2 text-center text-[#A3A3A3]">{m.bodyFat ? `${m.bodyFat}%` : '—'}</td>
                        <td className="py-3 px-2 text-center text-[#A3A3A3] hidden sm:table-cell">{m.muscleMass ? `${m.muscleMass}kg` : '—'}</td>
                        <td className="py-3 px-2 text-center text-[#A3A3A3] hidden sm:table-cell">{m.waist ? `${m.waist}cm` : '—'}</td>
                        <td className="py-3 px-2 text-center text-[#A3A3A3] hidden sm:table-cell">{m.hip ? `${m.hip}cm` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ── CALENDÁRIO ── */}
        {tab === 'calendar' && (
          <CalendarView workoutAssignments={workoutAssignments} dietAssignments={dietAssignments} />
        )}
      </div>
    </div>
  );
}
